import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import FormInputField from 'src/components/common/formInputField';
import { QuestionArrayModel, ScrollQuestionModel } from 'src/models/videosModel';
import { useSettingActions } from 'src/store/settings/actions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/reducers/state';
import TimePicker from 'rc-time-picker';
import 'rc-time-picker/assets/index.css';
import moment from 'moment';
import Loader from 'src/components/loader';

const UpdateQuizModal = () => {
  const dispatch = useDispatch();
  const settingAction = useSettingActions(dispatch);
  const ref = useRef<HTMLButtonElement>(null);
  const [questionArray, setQuestionArray] = useState<Array<QuestionArrayModel>>([
    { type: 'qt1', qId: 1, option: [], description: false }
  ]);
  const [questionId, setQuestionId] = useState(2);
  const [questionCount, setQuestionCount] = useState(1);
  const [scrollQuestion, setScrollQuestion] = useState<ScrollQuestionModel[]>([]);
  const [displayResultData, setDisplayResultData] = useState(false);
  const [answerSelectError, setAnswerSelectError] = useState<boolean>(false);
  const [isDataSubmitted, setIsDataSubmitted] = useState<boolean>(false);
  const [totalPoints, setTotalPoints] = useState<number>(1);
  const [time, setTime] = useState<any>('00:00:00');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    unregister
  } = useForm();

  const { singlePollTemplate, isTemplateUpdated, isTemplateUpdatedLoading } = useSelector(
    (state: RootState) => {
      return {
        singlePollTemplate: state.setting.singlePollTemplate,
        isTemplateUpdated: state.setting.isTemplateUpdated,
        isTemplateUpdatedLoading: state.setting.isTemplateUpdatedLoading
      };
    }
  );

  const onChange = (e: any) => {
    setTime(moment(e).format('HH:mm:ss'));
  };

  useEffect(() => {
    if (singlePollTemplate && singlePollTemplate?.pollType == 0) {
      const scrollQuestionReset: Array<ScrollQuestionModel> = [];
      const questionArr: Array<QuestionArrayModel> = [];
      const resetAnswerData: any = {};
      const totalPoints: Array<number> = [];
      singlePollTemplate?.quizzes?.questions?.forEach((item: any, index: number) => {
        if (!item.quizOpenTextQuestion) {
          const choiceData =
            item.choices?.map((choice: any, idx: number) => {
              resetAnswerData[`option_${index + 1}_${idx}`] = choice.answerText;
              return { option: `option_${index + 1}_${idx}`, isAnswer: choice.isAnswer };
            }) || [];

          questionArr.push({
            type: 'qt1',
            qId: index + 1,
            option: choiceData,
            description: item.description ? true : false
          });

          resetAnswerData[`question_${index + 1}`] = item.questionText;
          resetAnswerData[`points_${index + 1}`] = item.points;
          resetAnswerData[`description_${index + 1}`] = item.description;
        } else {
          questionArr.push({
            type: 'qt2',
            qId: index + 1,
            option: [],
            description: item.description ? true : false,
            allowResourceUpload: item.allowResourceUpload,
            isTextAnswer: item.isTextAnswer
          });

          resetAnswerData[`question_${index + 1}`] = item.questionText;
          resetAnswerData[`points_${index + 1}`] = item.points;
          resetAnswerData[`description_${index + 1}`] = item.description;
          resetAnswerData[`text_answer_${index + 1}`] = item.textAnswer;
        }

        scrollQuestionReset.push({ name: item.questionText, id: index + 1 });
        totalPoints.push(item.points);
      });

      setQuestionArray(questionArr);
      setScrollQuestion(scrollQuestionReset);
      setQuestionCount(questionArr.length);
      setQuestionId(questionArr.length + 1);
      setTotalPoints(totalPoints.reduce((point1, point2) => point1 + point2, 0));
      setDisplayResultData(singlePollTemplate.displayAtEnd);
      setTime(singlePollTemplate.pollDisplayTime || '00:00:00');
      const resetData = {
        quizname: singlePollTemplate.templateName,
        pollType: singlePollTemplate.pollType,
        displayResultsTimer: singlePollTemplate.displayResultsTimer,
        ...resetAnswerData
      };
      reset(resetData);
    }
  }, [singlePollTemplate, reset, setValue]);

  const handleAddOptionClick = (qId: number) => {
    const finalData = questionArray.map((item: QuestionArrayModel) => {
      if (item.qId === qId && item.type === 'qt1') {
        item.option.push({ option: `option_${qId}_${item.option.length + 1}`, isAnswer: false });
      }
      return item;
    });
    setQuestionArray(finalData);
  };

  const handleCloseQuestion = (id: number) => {
    const filteredData = questionArray.filter((item: QuestionArrayModel) => item.qId !== id);
    unregister([`question_${id}`, `points_${id}`, `description_${id}`]);
    setQuestionArray(filteredData);
    setScrollQuestion(scrollQuestion.filter((question: ScrollQuestionModel) => question.id !== id));
    setQuestionCount((prev) => prev - 1);
  };

  const handleAddQuestionClick = () => {
    setIsDataSubmitted(false);
    setQuestionId((x) => x + 1);
    setQuestionArray([
      ...questionArray,
      { type: 'qt1', qId: questionId, option: [], description: false }
    ]);
    setQuestionCount((x) => x + 1);
  };

  const handleAddOpenTextQuestion = () => {
    setIsDataSubmitted(false);
    setQuestionId((x) => x + 1);
    setQuestionArray([
      ...questionArray,
      { type: 'qt2', qId: questionId, option: [], description: false, isTextAnswer: false }
    ]);
    setQuestionCount((x) => x + 1);
  };

  const handleDescriptionClick = (qId: number) => {
    const finalData = questionArray.map((item: QuestionArrayModel) => {
      if (item.qId === qId) {
        item.description = true;
      }
      return item;
    });
    setQuestionArray(finalData);
  };

  const handleIsAnsweClick = (qId: number, optionId: number) => {
    const finalData = questionArray.map((item: QuestionArrayModel) => {
      if (item.qId === qId) {
        item.option.forEach((option, index) => {
          option.isAnswer = index === optionId;
        });
      }
      return item;
    });
    setQuestionArray(finalData);
  };

  const handleIsAnswerTextClick = (qId: number) => {
    const finalData = questionArray.map((item: QuestionArrayModel) => {
      if (item.qId === qId && item.type === 'qt2') {
        item.isTextAnswer = !item.isTextAnswer;
      }
      return item;
    });
    setQuestionArray(finalData);
  };

  const handleCloseClick = () => {
    setTime('00:00:00');
    setQuestionArray([{ type: 'qt1', qId: 1, option: [], description: false }]);
    setScrollQuestion([]);
    setQuestionCount(1);
    setQuestionId(2);
    setTotalPoints(1);
    setDisplayResultData(false);
    setIsDataSubmitted(false);

    reset({
      quizname: '',
      pollType: 0,
      displayResultsTimer: '',
      question_1: '',
      points_1: 1
    });
  };

  const onSubmit = (data: any) => {
    setIsDataSubmitted(true);
    const question = [];
    for (let i = 1; i <= questionCount; i++) {
      if (data[`question_${i}`]) {
        const choiceData = questionArray.find((item) => item.qId === i);
        const choiceArray: any = [];
        if (choiceData?.type === 'qt1') {
          choiceData.option.forEach((option, j) => {
            const optionKey = `${option.option}`;
            if (data[optionKey] !== undefined && data[optionKey] !== '') {
              choiceArray.push({
                position: j + 1,
                answerText: data[optionKey],
                isNewOption: true,
                isAnswer: option.isAnswer
              });
            }
          });
        }
        const finalQuestionData = {
          position: i,
          description: data[`description_${i}`] || '',
          questionText: data[`question_${i}`],
          points: data[`points_${i}`] ? parseFloat(data[`points_${i}`]) : 1,
          allowResourceUpload: data[`allowResourceUpload_${i}`] || false,
          isNewQuestion: true,
          quizOpenTextQuestion: choiceData?.type === 'qt2',
          choices: choiceArray,
          isTextAnswer: data[`isTextAnswer_${i}`] || false,
          textAnswer: data[`text_answer_${i}`] || null
        };
        question.push(finalQuestionData);
      }
    }

    const payloadData: any = {
      pollId: singlePollTemplate.pollId,
      templateId: singlePollTemplate?.templateId,
      templateName: data.quizname,
      pollType: data.pollType || 0,
      pollDisplayTimeString: time,
      displayResults: displayResultData,
      displayResultsTimer: data.displayResultsTimer,
      quizzes: {
        id: singlePollTemplate?.quizzes?.id,
        pollId: singlePollTemplate?.quizzes?.pollId,
        pollType: singlePollTemplate?.quizzes?.pollType,
        name: data.quizname,
        questions: question
      }
    };

    if (!answerSelectError) {
      settingAction.updateTemplateRequest(payloadData);
    }
  };
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name?.includes('question')) {
        const questionText: Array<ScrollQuestionModel> = [];
        questionArray.forEach((item) => {
          questionText.push({ name: watch(`question_${item.qId}`), id: item.qId });
        });
        setScrollQuestion(questionText);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name?.includes('points')) {
        const points = questionArray
          .map((item) => watch(`points_${item.qId}`))
          .filter((point) => point !== '')
          .reduce((sum, point) => sum + parseFloat(point || '0'), 0);
        setTotalPoints(points);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    const conditionArray: Array<boolean> = [];
    if (totalPoints === 0) {
      setAnswerSelectError(false);
    } else {
      const questionTypeOne = questionArray.filter((item) => item.type === 'qt1');
      questionTypeOne.forEach((question, qIndex) => {
        const hasAnswer = question.option.some((option) => option.isAnswer);
        conditionArray.push(!hasAnswer);
      });
      setAnswerSelectError(conditionArray.includes(true));
    }
  }, [questionArray, totalPoints]);

  useEffect(() => {
    if (isTemplateUpdated) {
      if (ref.current) {
        ref.current.click();
      }
      handleCloseClick();
    }
  }, [isTemplateUpdated]);

  return (
    <div>
      <div
        className="modal fade"
        id="UpdateQuizModal"
        aria-labelledby="UpdateQuizModalLabel"
        aria-hidden="true">
        <div className="modal-dialog relative w-auto pointer-events-none max-w-xl">
          <div className="modal-content">
            <div className="relative modal-header flex flex-shrink-0 items-center justify-between p-4 rounded-t-md">
              <h5
                className="text-xl font-semibold leading-normal text-center w-full"
                id="UpdateQuizModalLabel">
                Quiz
              </h5>
              <button
                ref={ref}
                onClick={handleCloseClick}
                type="button"
                className="dismiss-btn"
                data-bs-dismiss="modal"
                aria-label="Close"></button>
            </div>
            <form className="px-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body relative py-4 border-b">
                <select disabled className="input-style w-full mb-5" {...register('pollType')}>
                  <option value={0}>Quiz</option>
                  <option value={1}>Multiple Choice</option>
                  <option value={2}>Open Text</option>
                  <option value={3}>Star Rating</option>
                </select>
                <div className="flex items-center justify-center">
                  <div className="form-group mb-3 w-full mr-3">
                    <label className="block text-sm font-medium mb-1">
                      Poll Display Time (hh:mm:ss)
                    </label>
                    <TimePicker
                      onChange={onChange}
                      value={time ? moment(time, 'HH:mm:ss') : undefined}
                    />
                  </div>
                  <div className="form-group mb-3 w-full ml-3">
                    <label className="block text-sm font-medium mb-1">Display At End</label>
                    <div className="form-check form-switch pl-0 mr-3 relative">
                      <input
                        onChange={(e) => setDisplayResultData(e.target.checked)}
                        className="form-check-input switch-btn"
                        type="checkbox"
                        role="switch"
                        id="displayResults"
                        checked={displayResultData}
                      />
                      <i className="fas fa-check checkbox-tick hidden"></i>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <div className={`form-group mb-3 w-full ${displayResultData ? '' : 'hidden'}`}>
                    <label className="block text-sm font-medium mb-1">Display Result Timer</label>
                    <select className="input-style" {...register('displayResultsTimer')}>
                      <option value={15}>15</option>
                      <option value={30}>30</option>
                      <option value={45}>45</option>
                      <option value={60}>60</option>
                      <option value={90}>90</option>
                      <option value={120}>120</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end">
                  <p className="font-bold">
                    Total Question Points: <span className="ml-1">{totalPoints}</span>
                  </p>
                </div>
                <div className="lg:grid lg:grid-cols-2 gap-6 mt-3">
                  <div className="border rounded-md">
                    <div className="px-4 py-3">
                      <div className="form-group mb-3 w-full">
                        <FormInputField
                          label={'Quiz Name'}
                          type={'text'}
                          fieldName={'quizname'}
                          register={register}
                          errors={errors}
                          isRequired={true}
                        />
                      </div>
                      <div className="p-6 rounded-md shadow border bg-white max-w-sm flex flex-col">
                        <ul>
                          {scrollQuestion.map((item: ScrollQuestionModel) => (
                            <li key={item.id} className="quiz-question-list">
                              <a href={`#Question${item.id}`} data-abc="true">
                                {item.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="border rounded-md">
                    <div className="flex items-center justify-between border-b px-5 py-4">
                      <p className="font-semibold mb-0">Questions</p>
                      <button
                        type="button"
                        id="actions-dropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        className="btn-primary w-auto mt-0 mr-0 ml-auto borderonly-btn">
                        Add Question
                      </button>
                      <ul
                        className="dropdown-menu dropdown-menu-ul w-32"
                        aria-labelledby="actions-dropdown">
                        <li className="cursor-pointer" onClick={handleAddQuestionClick}>
                          <span className="action-menu">Add Question</span>
                        </li>
                        <li className="cursor-pointer" onClick={handleAddOpenTextQuestion}>
                          <span className="action-menu">Add Open Text Question</span>
                        </li>
                      </ul>
                    </div>
                    {questionArray.map((item, index) => (
                      <div
                        id={`Question${index + 1}`}
                        key={index}
                        className="p-4 questions-side-card">
                        <div className="flex-items">
                          <label className="font-semibold">Question {item.qId}</label>
                          <div className="dropdown relative">
                            <button
                              className="dropdown-toggle border-0 input-style mx-auto flex-items"
                              type="button"
                              id="action-dropdown"
                              data-bs-toggle="dropdown"
                              aria-expanded="false">
                              <i className="fas fa-chevron-down"></i>
                            </button>
                            <ul
                              className="dropdown-menu dropdown-menu-ul w-32"
                              aria-labelledby="action-dropdown">
                              <li
                                className="cursor-pointer"
                                onClick={() => handleDescriptionClick(item.qId)}>
                                <span className="action-menu">Add description</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                        <div className="form-group mb-3 w-full relative">
                          <i
                            id={`${item.qId}`}
                            className="fas fa-times absolute right-0 top-4 cursor-pointer"
                            onClick={() => handleCloseQuestion(item.qId)}></i>
                          <input
                            type="text"
                            className="input-style pl-0"
                            placeholder="What would you like to ask ?"
                            {...register(`question_${item.qId}`)}
                          />
                        </div>
                        {item.description && (
                          <div className="form-group mb-3 w-full">
                            <input
                              type="text"
                              className="input-style pl-0"
                              placeholder="Description (for participants)"
                              {...register(`description_${item.qId}`)}
                            />
                          </div>
                        )}
                        {item.type === 'qt1' && (
                          <button
                            id={`addOption_${item.qId}`}
                            type="button"
                            className="btn-primary w-auto mx-0 ml-auto"
                            onClick={() => handleAddOptionClick(item.qId)}>
                            <i className="fas fa-plus"></i>
                          </button>
                        )}
                        {item.option.map((option, index) => (
                          <div key={index} className="form-group mb-3 w-full">
                            <div className="flex items-center mr-3">
                              <div className="form-check">
                                <input
                                  id={`is_Answer_${option.option}_${index}`}
                                  onClick={() => handleIsAnsweClick(item.qId, index)}
                                  name={`is_Answer_${option.option}`}
                                  className="form-check-input radio-btn"
                                  type="radio"
                                  checked={option.isAnswer}
                                />
                              </div>
                              <input
                                type="text"
                                className="input-style pl-0 ml-3"
                                placeholder="Add Option"
                                id={`${option.option}`}
                                {...register(`${option.option}`)}
                              />
                            </div>
                          </div>
                        ))}
                        {item.type === 'qt2' && (
                          <>
                            <div className="lg:flex my-5 justify-between">
                              <div className="lg:w-1/2 pb-0">
                                <h6 className="font-bold">Allow Resource Upload</h6>
                              </div>
                              <div className="lg:w-1/2 pb-0">
                                <div className="form-check form-switch pl-0 flex items-center justify-end">
                                  <div className="relative">
                                    <input
                                      className="form-check-input switch-btn"
                                      type="checkbox"
                                      role="switch"
                                      id="allowResourceUpload"
                                      defaultChecked={item.allowResourceUpload}
                                      {...register(`allowResourceUpload_${item.qId}`)}
                                    />
                                    <i className="fas fa-check checkbox-tick hidden"></i>
                                    <i className="fas fa-times checkbox-cross"></i>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="lg:flex my-5 justify-between">
                              <div className="lg:w-1/2 pb-0">
                                <h6 className="font-bold">Text Answer</h6>
                              </div>
                              <div className="lg:w-1/2 pb-0">
                                <div className="form-check form-switch pl-0 flex items-center justify-end">
                                  <div className="relative">
                                    <input
                                      className="form-check-input switch-btn"
                                      type="checkbox"
                                      role="switch"
                                      id="isTextAnswer"
                                      defaultChecked={item.isTextAnswer}
                                      {...register(`isTextAnswer_${item.qId}`, {
                                        onChange: () => handleIsAnswerTextClick(item.qId)
                                      })}
                                    />
                                    <i className="fas fa-check checkbox-tick hidden"></i>
                                    <i className="fas fa-times checkbox-cross"></i>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {item.isTextAnswer && (
                              <div className="form-group mb-3 w-full relative">
                                <input
                                  type="text"
                                  className="input-style pl-0"
                                  placeholder="Enter Answer"
                                  {...register(`text_answer_${item.qId}`)}
                                />
                              </div>
                            )}
                          </>
                        )}
                        <div className="flex items-center mr-3">
                          <label className="font-semibold">Points</label>
                          <input
                            type="text"
                            className="input-style pl-0 w-1/2 ml-2"
                            defaultValue={1}
                            {...register(`points_${item.qId}`)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-center mt-1">
                {answerSelectError && isDataSubmitted && (
                  <p className="text-red-500 text-xs error">Please select Answer in Question</p>
                )}
              </div>
              <div className="modal-footer flex flex-shrink-0 flex-wrap items-center justify-center p-4 py-6 rounded-b-md">
                <button
                  disabled={isTemplateUpdatedLoading || isDataSubmitted}
                  type="submit"
                  className="px-6 btn-primary w-auto whitespace-nowrap mr-2 ml-0 mt-0">
                  Save Changes
                </button>
                <button
                  onClick={handleCloseClick}
                  type="button"
                  className="mx-0 px-6 btn-primary discard-btn w-auto whitespace-nowrap mt-0"
                  data-bs-dismiss="modal">
                  Close
                </button>
                <span className="ml-1">{isTemplateUpdatedLoading && <Loader />}</span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateQuizModal;
